import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import axios from 'axios';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const ValidationsTab = () => {
  const [entreprisesEnAttentePaiement, setEntreprisesEnAttentePaiement] = useState([]);
  const [entreprisesEnRevueKYB, setEntreprisesEnRevueKYB] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedEntreprise, setSelectedEntreprise] = useState(null);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [paymentNotes, setPaymentNotes] = useState('');
  const [activeTab, setActiveTab] = useState('paiement'); // 'paiement' ou 'validation'

  useEffect(() => {
    fetchEntreprises();
  }, []);

  const fetchEntreprises = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/admin/entreprises`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Séparer les entreprises selon leur statut
      const enAttentePaiement = response.data.filter(e => e.statut === 'EN_ATTENTE_PAIEMENT');
      const enRevueKYB = response.data.filter(e => e.statut === 'EN_REVUE_KYB');
      
      setEntreprisesEnAttentePaiement(enAttentePaiement);
      setEntreprisesEnRevueKYB(enRevueKYB);
      setLoading(false);
    } catch (err) {
      console.error('Erreur lors du chargement des entreprises:', err);
      setError('Erreur lors du chargement des entreprises');
      setLoading(false);
    }
  };

  const handleMarquerPaiement = async () => {
    if (!selectedEntreprise) return;

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/entreprises/${selectedEntreprise.id}/marquer-paiement`,
        { notes: paymentNotes },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('✅ Paiement marqué comme reçu ! L\'entreprise passe en revue KYB.');
      setShowPaymentModal(false);
      setPaymentNotes('');
      setSelectedEntreprise(null);
      fetchEntreprises();
    } catch (err) {
      console.error('Erreur lors du marquage du paiement:', err);
      toast.error('❌ Erreur lors du marquage du paiement');
    }
  };

  const handleApprove = async (entrepriseId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/entreprises/${entrepriseId}/valider`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('✅ Entreprise validée avec succès !');
      fetchEntreprises();
    } catch (err) {
      console.error('Erreur lors de la validation:', err);
      toast.error('❌ Erreur lors de la validation de l\'entreprise');
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      toast.warning('⚠️ Veuillez indiquer un motif de rejet');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/entreprises/${selectedEntreprise.id}/rejeter`,
        { raison: rejectReason },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('✅ Entreprise rejetée');
      setShowRejectModal(false);
      setRejectReason('');
      setSelectedEntreprise(null);
      fetchEntreprises();
    } catch (err) {
      console.error('Erreur lors du rejet:', err);
      toast.error('❌ Erreur lors du rejet de l\'entreprise');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-GN', {
      style: 'currency',
      currency: 'GNF',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-gray-500">Chargement...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 text-red-700 rounded">
        {error}
      </div>
    );
  }

  const renderEntrepriseCard = (entreprise, showPaymentButton = false, showValidationButtons = false) => (
    <Card key={entreprise.id} className="border-2 border-orange-200">
      <CardHeader className="bg-gradient-to-r from-purple-50 to-orange-50">
        <CardTitle className="flex items-center justify-between">
          <span className="text-purple-700">{entreprise.raison_sociale}</span>
          <div className="flex items-center gap-2">
            {entreprise.paiement_recu && (
              <span className="px-3 py-1 bg-green-100 text-green-700 text-xs font-semibold rounded-full">
                ✅ PAYÉ
              </span>
            )}
            <span className={`px-3 py-1 text-sm font-semibold rounded-full ${
              entreprise.statut === 'EN_ATTENTE_PAIEMENT' 
                ? 'bg-yellow-100 text-yellow-700' 
                : 'bg-blue-100 text-blue-700'
            }`}>
              {entreprise.statut === 'EN_ATTENTE_PAIEMENT' ? '⏳ EN ATTENTE PAIEMENT' : '🔍 EN REVUE KYB'}
            </span>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent className="p-6">
        <div className="grid md:grid-cols-2 gap-6">
          {/* Informations de l'entreprise */}
          <div className="space-y-3">
            <h4 className="font-semibold text-purple-700 mb-3">📋 Informations</h4>
            <div className="space-y-2 text-sm">
              <div>
                <span className="text-gray-600">RCCM :</span>
                <span className="ml-2 font-semibold">{entreprise.rccm}</span>
              </div>
              {entreprise.nif && (
                <div>
                  <span className="text-gray-600">NIF :</span>
                  <span className="ml-2 font-semibold">{entreprise.nif}</span>
                </div>
              )}
              <div>
                <span className="text-gray-600">Adresse :</span>
                <span className="ml-2">{entreprise.adresse}</span>
              </div>
              <div>
                <span className="text-gray-600">Mode de facturation :</span>
                <span className="ml-2 font-semibold">{entreprise.mode_facturation}</span>
              </div>
              <div>
                <span className="text-gray-600">Date d'inscription :</span>
                <span className="ml-2">{formatDate(entreprise.created_at)}</span>
              </div>
            </div>
          </div>

          {/* Contact */}
          <div className="space-y-3">
            <h4 className="font-semibold text-purple-700 mb-3">👤 Contact principal</h4>
            <div className="space-y-2 text-sm">
              <div>
                <span className="text-gray-600">Nom :</span>
                <span className="ml-2 font-semibold">{entreprise.contact_nom}</span>
              </div>
              <div>
                <span className="text-gray-600">Email :</span>
                <span className="ml-2">{entreprise.contact_email}</span>
              </div>
              <div>
                <span className="text-gray-600">Téléphone :</span>
                <span className="ml-2">{entreprise.contact_telephone}</span>
              </div>
              {entreprise.paiement_recu && entreprise.date_paiement && (
                <div className="mt-3 p-2 bg-green-50 rounded border-l-4 border-green-500">
                  <div>
                    <span className="text-gray-600">💰 Paiement reçu le :</span>
                    <span className="ml-2 font-semibold text-green-700">
                      {formatDate(entreprise.date_paiement)}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Frais de déploiement */}
        <div className="mt-6 p-4 bg-gradient-to-r from-purple-100 to-orange-100 rounded-lg border-2 border-purple-300">
          <div className="flex justify-between items-center">
            <div>
              <p className="text-sm text-gray-700">Frais de déploiement annuels</p>
              <p className="text-xs text-gray-600 mt-1">
                {entreprise.nb_employes || 0} employé(s) déclaré(s)
              </p>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold text-orange-600">
                {formatCurrency(entreprise.frais_deploiement || 3000000)}
              </p>
              <p className="text-xs text-gray-600">par an</p>
            </div>
          </div>
        </div>

        {/* Boutons d'action */}
        <div className="mt-6 flex gap-3">
          {showPaymentButton && (
            <Button
              onClick={() => {
                setSelectedEntreprise(entreprise);
                setShowPaymentModal(true);
              }}
              className="flex-1 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white font-semibold"
            >
              💰 Marquer comme payé
            </Button>
          )}
          
          {showValidationButtons && (
            <>
              <Button
                onClick={() => handleApprove(entreprise.id)}
                className="flex-1 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white font-semibold"
              >
                ✅ Valider l'inscription
              </Button>
              <Button
                onClick={() => {
                  setSelectedEntreprise(entreprise);
                  setShowRejectModal(true);
                }}
                className="flex-1 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white font-semibold"
              >
                ❌ Rejeter
              </Button>
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-6">
      <ToastContainer position="top-right" autoClose={3000} />
      
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-orange-500">
            Gestion des inscriptions entreprises
          </h2>
          <p className="text-gray-600 mt-1">
            Paiements en attente : {entreprisesEnAttentePaiement.length} | 
            Validations en attente : {entreprisesEnRevueKYB.length}
          </p>
        </div>
      </div>

      {/* Onglets */}
      <div className="flex gap-2 border-b-2 border-gray-200">
        <button
          onClick={() => setActiveTab('paiement')}
          className={`px-6 py-3 font-semibold transition-all ${
            activeTab === 'paiement'
              ? 'border-b-4 border-orange-500 text-orange-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          💰 En attente de paiement ({entreprisesEnAttentePaiement.length})
        </button>
        <button
          onClick={() => setActiveTab('validation')}
          className={`px-6 py-3 font-semibold transition-all ${
            activeTab === 'validation'
              ? 'border-b-4 border-purple-500 text-purple-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          🔍 En attente de validation ({entreprisesEnRevueKYB.length})
        </button>
      </div>

      {/* Contenu des onglets */}
      {activeTab === 'paiement' && (
        <div className="space-y-4">
          {entreprisesEnAttentePaiement.length === 0 ? (
            <Card>
              <CardContent className="p-12 text-center">
                <div className="text-6xl mb-4">💰</div>
                <h3 className="text-xl font-semibold text-gray-700 mb-2">
                  Aucune entreprise en attente de paiement
                </h3>
                <p className="text-gray-500">
                  Toutes les entreprises inscrites ont effectué leur paiement.
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {entreprisesEnAttentePaiement.map((entreprise) => 
                renderEntrepriseCard(entreprise, true, false)
              )}
            </div>
          )}
        </div>
      )}

      {activeTab === 'validation' && (
        <div className="space-y-4">
          {entreprisesEnRevueKYB.length === 0 ? (
            <Card>
              <CardContent className="p-12 text-center">
                <div className="text-6xl mb-4">✅</div>
                <h3 className="text-xl font-semibold text-gray-700 mb-2">
                  Aucune entreprise en attente de validation
                </h3>
                <p className="text-gray-500">
                  Toutes les demandes d'inscription ont été traitées.
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {entreprisesEnRevueKYB.map((entreprise) => 
                renderEntrepriseCard(entreprise, false, true)
              )}
            </div>
          )}
        </div>
      )}

      {/* Modal de marquage de paiement */}
      {showPaymentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-green-700 mb-4">
              💰 Marquer le paiement comme reçu
            </h3>
            <p className="text-gray-600 mb-4">
              Entreprise : <strong>{selectedEntreprise?.raison_sociale}</strong>
            </p>
            <p className="text-sm text-gray-600 mb-4">
              Montant : <strong className="text-orange-600">
                {formatCurrency(selectedEntreprise?.frais_deploiement || 3000000)}
              </strong>
            </p>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Notes (optionnel)
            </label>
            <textarea
              value={paymentNotes}
              onChange={(e) => setPaymentNotes(e.target.value)}
              placeholder="Référence bancaire, mode de paiement, etc..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              rows="3"
            />
            <div className="mt-4 p-3 bg-blue-50 border-l-4 border-blue-500 rounded">
              <p className="text-xs text-blue-700">
                ℹ️ En marquant le paiement comme reçu, l'entreprise passera automatiquement 
                en statut "EN_REVUE_KYB" et apparaîtra dans l'onglet "Validation".
              </p>
            </div>
            <div className="mt-6 flex gap-3">
              <Button
                onClick={() => {
                  setShowPaymentModal(false);
                  setPaymentNotes('');
                  setSelectedEntreprise(null);
                }}
                variant="outline"
                className="flex-1"
              >
                Annuler
              </Button>
              <Button
                onClick={handleMarquerPaiement}
                className="flex-1 bg-green-600 hover:bg-green-700 text-white"
              >
                Confirmer la réception
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de rejet */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-red-700 mb-4">
              ❌ Rejeter l'inscription
            </h3>
            <p className="text-gray-600 mb-4">
              Entreprise : <strong>{selectedEntreprise?.raison_sociale}</strong>
            </p>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Motif du rejet *
            </label>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Indiquez la raison du rejet..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
              rows="4"
            />
            <div className="mt-6 flex gap-3">
              <Button
                onClick={() => {
                  setShowRejectModal(false);
                  setRejectReason('');
                  setSelectedEntreprise(null);
                }}
                variant="outline"
                className="flex-1"
              >
                Annuler
              </Button>
              <Button
                onClick={handleReject}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white"
              >
                Confirmer le rejet
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ValidationsTab;
